import React, { useState, useEffect, useContext } from 'react';
import { formatDateDMY } from '../utils/dateUtils';
import { useNavigate, useLocation } from 'react-router-dom';
import ClientLink from '../components/ClientLink';
import axios from 'axios';
import { apiUrl } from '../config/api';
import { AuthContext } from '../context/AuthContext';
import LoanEditForm from '../components/Forms/LoanEditForm';
import LoanDetails from '../components/Forms/LoanDetails';
import FullScreenPanel from '../components/UI/FullScreenPanel';
import InvoiceModal from '../components/InvoiceModal';
import UndoRepaymentConfirm from '../components/UndoRepaymentConfirm';
import Button from '../components/UI/Button';
import ActionButton from '../components/UI/ActionButton';
import KpiCard from '../components/Admin/KpiCard';
import { loansMetrics, owesBalance, toCurrency } from '../utils/adminMetrics';
import { exportToCsv } from '../utils/adminExport';
import ResponsiveTable from '../components/ResponsiveTable';
import StatusBadge from '../components/UI/StatusBadge';
import AffordabilityBadge from '../components/affordability/AffordabilityBadge';
import { useTableSort, sortRows } from '../components/UI/tableSort';
import LoansStatusDropdown from '../components/LoansStatusDropdown';
import WriteOffCandidatesPanel from '../components/WriteOffCandidatesPanel';
import { enrichLoanForInvoice as enrichInvoiceLoan } from '../utils/invoiceUtils';
import Pager from '../components/UI/Pager';
// The calculator's legal decision (Microlending Act / ML.S.4) as a badge.
const CALCULATOR_BADGE = {
  'DECLINED': ['Not affordable', 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800/60'],
  'REFERRED FOR REVIEW': ['Check first', 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800/60'],
  'APPROVED WITH REDUCED AMOUNT': ['Lower amount', 'bg-sky-50 text-sky-800 border-sky-200 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-800/60'],
  'APPROVED': ['Qualifies', 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800/60'],
};

// Heroicons SVGs for section icons
const UserIcon = () => (
  <svg className="w-6 h-6 text-blue-600 mr-2 inline-block" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <circle cx="12" cy="8" r="4" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 20v-2a6 6 0 0112 0v2" />
  </svg>
);
const CashIcon = () => (
  <svg className="w-6 h-6 text-yellow-600 mr-2 inline-block" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <rect width="20" height="12" x="2" y="6" rx="2" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const RepaymentIcon = () => (
  <svg className="w-6 h-6 text-green-600 mr-2 inline-block" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3" />
    <circle cx="12" cy="12" r="10" />
  </svg>
);

// Repayment Form Popout
const RepaymentForm = ({ loan, clientId, onClose, onSuccess }) => {
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [refTouched, setRefTouched] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        apiUrl(`/api/loans/${loan.id}/repayments`),
        { 
          client_id: clientId, 
          amount_paid: amount, 
          payment_date: date,
          payment_method: paymentMethod,
          payment_reference: paymentReference.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage({ type: 'success', text: 'Repayment entered successfully!' });
      setAmount('');
      setDate('');
      setPaymentMethod('');
      setPaymentReference('');
      setRefTouched(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      const apiError = err?.response?.data?.error || err?.response?.data?.message || err.message || 'Error entering repayment.';
      setMessage({ type: 'error', text: apiError });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 rounded-xl overflow-hidden w-full max-w-md"
        style={{ boxShadow: '0 20px 40px -8px rgba(0,0,0,0.18), 0 4px 16px -4px rgba(22,163,74,0.12)' }}
      >
        <div className="h-1" style={{ background: 'linear-gradient(90deg, #16a34a, #059669, #0891b2)' }} />
        <div className="px-6 py-4 border-b border-green-100/60 dark:border-slate-700 flex items-center justify-between"
          style={{ background: 'linear-gradient(to right, #f9fafb, #f0fdf455)' }}
        >
          <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100 flex items-center gap-2">
            <span className="w-1 h-4 rounded-full bg-gradient-to-b from-green-500 to-emerald-600 inline-block flex-shrink-0" />
            <RepaymentIcon /> Enter Repayment
          </h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full text-gray-400 dark:text-slate-400 hover:bg-green-100/60 dark:hover:bg-slate-700 hover:text-green-700 dark:hover:text-slate-200 transition-colors"
            title="Close"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6 space-y-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-indigo-700 mb-1.5 uppercase tracking-wide">Amount</label>
              <input
                type="number"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                required
                className="w-full"
                min="0.01"
                step="0.01"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-indigo-700 mb-1.5 uppercase tracking-wide">Date</label>
              <input
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                required
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-indigo-700 mb-1.5 uppercase tracking-wide">Payment Method</label>
              <select
                value={paymentMethod}
                onChange={e => setPaymentMethod(e.target.value)}
                required
                className="w-full"
              >
                <option value="">Select method</option>
                <option value="Cash">Cash</option>
                <option value="EFT/Bank Transfer">EFT/Bank Transfer</option>
                <option value="Debit Order">Debit Order</option>
                <option value="Card (POS/Speedpoint)">Card (POS/Speedpoint)</option>
                <option value="PayToday">PayToday</option>
                <option value="PayPulse">PayPulse</option>
                <option value="PayPulse (Bank Windhoek)">PayPulse (Bank Windhoek)</option>
                <option value="BlueWallet">BlueWallet</option>
                <option value="FNB eWallet/Pay2Cell">FNB eWallet/Pay2Cell</option>
                <option value="ATM Cash Deposit">ATM Cash Deposit</option>
                <option value="Stop Order">Stop Order</option>
                <option value="MTC Pay">MTC Pay</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-indigo-700 mb-1.5 uppercase tracking-wide">Reference <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={paymentReference}
                onChange={e => setPaymentReference(e.target.value)}
                onBlur={() => setRefTouched(true)}
                placeholder="e.g., EFT ref, slip no., Txn ID"
                required
                className="w-full"
              />
              <p className={`mt-1 text-xs ${refTouched && !paymentReference.trim() ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-slate-400'}`}>
                {refTouched && !paymentReference.trim() ? 'Enter the payment reference.' : 'Required: the EFT reference, slip number or transaction ID.'}
            </p>
            </div>
            {message.text && (
              <div className={`p-3 rounded-lg text-sm text-center border ${message.type === 'error' ? 'bg-red-50 text-red-700 border-red-100 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800/40' : 'bg-green-50 text-green-700 border-green-100 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800/40'}`}>
                {message.text}
              </div>
            )}
            <div className="flex justify-end gap-3 pt-2 border-t border-green-50 dark:border-slate-700 mt-1">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors font-medium text-sm border border-gray-200 dark:border-slate-600"
              >
                Cancel
              </button>
              <ActionButton
                type="submit"
                onClick={(e) => {
                  const form = e.currentTarget.form;
                  if (form && typeof form.reportValidity === 'function' && !form.reportValidity()) return;
                  e.preventDefault();
                  return handleSubmit(e);
                }}
                busyLabel="Submitting…"
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-green-500"
              >
                Submit Repayment
              </ActionButton>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Loan Edit Form Popout


// Cancel Form component
const CancelForm = ({ loan, onClose, onSuccess }) => {
  const [cancelReason, setCancelReason] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(apiUrl(`/api/loans/${loan.id}/cancel`), { cancel_reason: cancelReason }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage('Loan cancelled successfully!');
      setTimeout(() => {
        if (onSuccess) onSuccess();
      }, 1000);
    } catch (err) {
      setMessage(err?.response?.data?.error || 'Error cancelling loan.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-orange-500 to-red-500" />
        <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700 bg-gray-50/60 dark:bg-slate-800/60 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100">Cancel Loan</h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full text-gray-400 dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-700 hover:text-gray-600 dark:hover:text-slate-200 transition-colors"
            title="Close"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 dark:text-slate-400 mb-1.5 uppercase tracking-wide">Cancellation Reason (required)</label>
              <textarea
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                className="w-full h-28 resize-none"
                placeholder="Enter reason for cancellation..."
                required
              />
            </div>
            <div className="flex justify-end gap-3 pt-1">
              <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition text-sm font-medium">Cancel</button>
              <ActionButton
                type="submit"
                onClick={(e) => {
                  const form = e.currentTarget.form;
                  if (form && typeof form.reportValidity === 'function' && !form.reportValidity()) return;
                  e.preventDefault();
                  return handleSubmit(e);
                }}
                busyLabel="Cancelling…"
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm font-medium shadow-sm"
              >
                Confirm Cancel
              </ActionButton>
            </div>
            {message && <div className="mt-2 text-sm text-green-700 dark:text-green-400 text-center">{message}</div>}
          </form>
        </div>
      </div>
    </div>
  );
};

// Terminate Contract Form component
const TerminateContractForm = ({ loan, onClose, onSuccess }) => {
  const [terminationNote, setTerminationNote] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        apiUrl(`/api/loans/${loan.id}/terminate`),
        { termination_note: terminationNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage('Contract terminated successfully!');
      setTimeout(() => {
        if (onSuccess) onSuccess();
      }, 1000);
    } catch (err) {
      const apiMsg = err?.response?.data?.error;
      setMessage(apiMsg || 'Error terminating contract.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-red-600 to-rose-500" />
        <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700 bg-gray-50/60 dark:bg-slate-800/60 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100">Terminate Contract</h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full text-gray-400 dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-700 hover:text-gray-600 dark:hover:text-slate-200 transition-colors"
            title="Close"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 dark:text-slate-400 mb-1.5 uppercase tracking-wide">Termination Note (required)</label>
              <textarea
                value={terminationNote}
                onChange={e => setTerminationNote(e.target.value)}
                className="w-full h-28 resize-none"
                placeholder="Enter termination note..."
                required
              />
            </div>
            <div className="flex justify-end gap-3 pt-1">
              <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition text-sm font-medium">Cancel</button>
              <ActionButton
                type="submit"
                onClick={(e) => {
                  const form = e.currentTarget.form;
                  if (form && typeof form.reportValidity === 'function' && !form.reportValidity()) return;
                  e.preventDefault();
                  return handleSubmit(e);
                }}
                busyLabel="Terminating…"
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm font-medium shadow-sm"
              >
                Confirm Terminate
              </ActionButton>
            </div>
            {message && <div className="mt-2 text-sm text-gray-700 dark:text-slate-300 text-center">{message}</div>}
          </form>
        </div>
      </div>
    </div>
  );
};


// Bad Debt / Write Off / Undo Write Off - confirmation modal
const LoanStatusConfirmModal = ({ kind, onClose, onConfirm, initialNote = '' }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState(initialNote || '');
  const [newDueDate, setNewDueDate] = useState('');

  const isReschedule = kind === 'reschedule';
  const noteRequired = kind === 'writeOff' || kind === 'undoWriteOff' || isReschedule;
  const trimmedNote = (note || '').trim();

  const meta = kind === 'writeOff'
    ? {
      title: 'Write off loan',
      message: 'Write off this loan? This will set status to "Written off".',
      confirmText: 'Yes, Write Off',
      confirmClass: 'bg-red-50 text-red-800 hover:bg-red-100',
      noteLabel: 'Write off note / reason (required)',
      notePlaceholder: 'Enter reason…'
    }
    : kind === 'undoWriteOff'
      ? {
        title: 'Undo write-off',
        message: 'Undo write-off for this loan? This will restore status to "Outstanding".',
        confirmText: 'Yes, Undo Write-Off',
        confirmClass: 'bg-amber-50 text-amber-800 hover:bg-amber-100',
        noteLabel: 'Undo write-off note / reason (required)',
        notePlaceholder: 'Enter reason…'
      }
      : isReschedule
        ? {
          title: 'Reschedule loan',
          message: 'Move this loan to a new due date. The loan stays active and the reschedule is recorded for NAMFISA reporting.',
          confirmText: 'Yes, Reschedule',
          confirmClass: 'bg-blue-50 text-blue-800 hover:bg-blue-100',
          noteLabel: 'Reschedule reason (required)',
          notePlaceholder: 'Enter reason…'
        }
        : {
          title: 'Mark loan as Bad Debt',
          message: 'Mark this loan as Bad Debt?',
          confirmText: 'Yes, Mark Bad Debt',
          confirmClass: 'bg-orange-50 text-orange-800 hover:bg-orange-100',
          noteLabel: 'Optional note / reason',
          notePlaceholder: 'Optional note…'
        };

  const handleConfirm = async () => {
    try {
      if (isReschedule && !newDueDate) {
        setError('A new due date is required.');
        return;
      }
      if (noteRequired && !trimmedNote) {
        setError('Note / reason is required.');
        return;
      }
      setSubmitting(true);
      setError('');
      await onConfirm(trimmedNote, { newDueDate });
    } catch (e) {
      const msg = e?.message || 'Action failed.';
      setError(msg);
      return;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 rounded-xl overflow-hidden w-full max-w-md"
        style={{ boxShadow: '0 20px 40px -8px rgba(0,0,0,0.18), 0 4px 16px -4px rgba(79,70,229,0.12)' }}
      >
        <div className="h-1" style={{ background: 'linear-gradient(90deg, #3b82f6, #6366f1, #8b5cf6)' }} />
        <div className="app-modal-header px-5 py-3.5 border-b border-indigo-100/60 dark:border-slate-700 flex items-center justify-between"
        >
          <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100 flex items-center gap-2">
            <span className="w-1 h-4 rounded-full bg-gradient-to-b from-blue-500 to-indigo-500 inline-block flex-shrink-0" />
            {meta.title}
          </h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full text-gray-400 dark:text-slate-400 hover:bg-indigo-100/60 dark:hover:bg-slate-700 hover:text-indigo-600 dark:hover:text-slate-200 transition-colors"
            title="Close"
            disabled={submitting}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-700 dark:text-slate-200">{meta.message}</p>

          {isReschedule && (
            <div>
              <label className="block text-xs font-semibold text-indigo-700 dark:text-indigo-400 mb-1.5 uppercase tracking-wide">New due date (required)</label>
              <input
                type="date"
                value={newDueDate}
                onChange={(e) => setNewDueDate(e.target.value)}
                className="w-full"
                disabled={submitting}
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-indigo-700 dark:text-indigo-400 mb-1.5 uppercase tracking-wide">{meta.noteLabel}</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full h-24 resize-none"
              placeholder={meta.notePlaceholder}
              disabled={submitting}
            />
          </div>

          {error && (
            <div className="p-2.5 rounded-lg border border-red-200 dark:border-red-800/40 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">{error}</div>
          )}

          <div className="flex justify-end gap-3 pt-2 border-t border-indigo-50 dark:border-slate-700">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors font-medium text-sm border border-gray-200 dark:border-slate-600"
            >
              No
            </button>
            <ActionButton
              type="button"
              onClick={handleConfirm}
              disabled={submitting || (noteRequired && !trimmedNote) || (isReschedule && !newDueDate)}
              className={`px-4 py-2 rounded-lg transition-colors font-medium text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1 ${meta.confirmClass}`}
            >
              {submitting ? 'Please wait…' : meta.confirmText}
            </ActionButton>
          </div>
        </div>
      </div>
    </div>
  );
};

const PAGE_SIZE = 10;
// Beside a client the panel is half the width and already sits below the
// documents row, so it shows a few loans at a time instead of a long list.
const EMBEDDED_PAGE_SIZE = 3;

// Loans workspace. Rendered standalone on its own route, and embedded inside the
// Clients page (side by side with a client) when a `clientId` is supplied. Only
// the presentation differs between the two — the loan rules are identical.
// Applications still being decided: nothing is signed off or paid out yet.
const IN_PROCESS_STATUSES = ['Pending', 'Awaiting Staff', 'Awaiting Client', 'Awaiting Approval'];

const ManageLoans = ({ clientId = null, embedded = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [clients, setClients] = useState([]);
  const [loans, setLoans] = useState([]);
  const [loansLoaded, setLoansLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  // Links from elsewhere (dashboard tiles, "Open full page") arrive as
  // /manage-loans?status=Overdue or ?loan=<id>; the status in the address
  // wins over the one remembered from last time.
  const [filterStatus, setFilterStatus] = useState(() => {
    if (!embedded) {
      const fromUrl = new URLSearchParams(window.location.search).get('status');
      if (fromUrl) return fromUrl;
    }
    try { return JSON.parse(localStorage.getItem('manage.filterStatus')) || 'All'; } catch { return 'All'; }
  });
  const [actionMessage, setActionMessage] = useState('');
  const [actionType, setActionType] = useState('info');
  const [showLoanDetails, setShowLoanDetails] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceLoan, setInvoiceLoan] = useState(null);
  const [contractsModal, setContractsModal] = useState(null);
  const [contractsModalPage, setContractsModalPage] = useState(1);
  const [contractsSort, setContractsSort] = useState(null);
  const [showRepaymentForm, setShowRepaymentForm] = useState(false);
  const [repaymentLoan, setRepaymentLoan] = useState(null);
  const [repaymentClientId, setRepaymentClientId] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editLoan, setEditLoan] = useState(null);
  const [editLoanDocuments, setEditLoanDocuments] = useState([]);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [editMessage, setEditMessage] = useState('');
  // Rejection form is now inline inside LoanDetails
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [cancelLoanTarget, setCancelLoanTarget] = useState(null);
  const [showTerminateForm, setShowTerminateForm] = useState(false);
  const [terminateLoanTarget, setTerminateLoanTarget] = useState(null);
  const [loanDocuments, setLoanDocuments] = useState(null);
  // removed interest rates state (was only used by removed Add Loan modal)
  const [moreDetailsNotes, setMoreDetailsNotes] = useState({}); // { [loanId]: note }
  const [showNoteInput, setShowNoteInput] = useState({}); // { [loanId]: boolean }
  // const [showAddLoan, setShowAddLoan] = useState(false); // removed Add Loan modal integration
  const [showUndoConfirm, setShowUndoConfirm] = useState(false);
  const [undoCtx, setUndoCtx] = useState(null); // { loanId, repaymentId, amount, displayDate }
  const [statusConfirmCtx, setStatusConfirmCtx] = useState(null); // { kind: 'badDebt' | 'writeOff' | 'undoWriteOff', loanId }

  useEffect(() => {
    const fetchLoans = async () => {
      try {
        const token = localStorage.getItem('token');
  const response = await axios.get(apiUrl('/api/loans/'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        setLoans(response.data);
      } catch (err) {
        setError('Error fetching loans: ' + err.message);
      } finally {
        setLoansLoaded(true);
      }
    };
    fetchLoans();
  }, []);

  // A status in the address (?status=...) sets the filter, also when the
  // address changes while the page is open.
  useEffect(() => {
    if (embedded) return;
    const fromUrl = new URLSearchParams(location.search).get('status');
    if (fromUrl) {
      setFilterStatus(fromUrl);
      setCurrentPage(1);
    }
  }, [location.search, embedded]);

  // Start at page 1 whenever the embedded client changes.
  useEffect(() => {
    setCurrentPage(1);
  }, [clientId]);

  // Persist filter selection
  useEffect(() => {
    try { localStorage.setItem('manage.filterStatus', JSON.stringify(filterStatus)); } catch {}
  }, [filterStatus]);

  // Permission helper (permission-based gating)
  const hasPerm = (perm) => Array.isArray(user?.permissions) && user.permissions.includes(perm);

  // In ManageLoans.jsx - update fetchLoanDocuments to match API { documents: [...] } shape
  const fetchLoanDocuments = async (loanId, loan) => {
    try {
      const token = localStorage.getItem('token');

      // Get client_id from the passed loan parameter instead of selectedLoan
      const clientId = loan.client_id;

      // Fetch both loan documents and client ID documents in parallel
      const [loanDocsResponse, clientDocsResponse] = await Promise.all([
        // Loan documents
        axios.get(apiUrl(`/api/document/loan/${loanId}`), {
          headers: { Authorization: `Bearer ${token}` },
        }),
        // Client documents
        axios.get(apiUrl(`/api/document/client/${clientId}`), {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      // Safely extract arrays regardless of whether API returns { documents: [...] } or [...]
      const loanDocs = Array.isArray(loanDocsResponse?.data?.documents)
        ? loanDocsResponse.data.documents
        : Array.isArray(loanDocsResponse?.data)
        ? loanDocsResponse.data
        : [];

      const clientDocs = Array.isArray(clientDocsResponse?.data?.documents)
        ? clientDocsResponse.data.documents
        : Array.isArray(clientDocsResponse?.data)
        ? clientDocsResponse.data
        : [];

      // Combine both types of documents and update state
      setLoanDocuments([...loanDocs, ...clientDocs]);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setLoanDocuments([]);
    }
  };

  const refreshData = async () => {
    try {
      const token = localStorage.getItem('token');
      const loansRes = await axios.get(apiUrl('/api/loans/'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLoans(loansRes.data);
    } catch (err) {
      // silent
    }
  };

  // Lightweight refresh: only reload the loans list (Manage Loans table)
  const refreshLoansOnly = async () => {
    try {
      const token = localStorage.getItem('token');
      const loansRes = await axios.get(apiUrl('/api/loans/'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLoans(loansRes.data);
    } catch (err) {
      // silent
    }
  };

  // body: { approve_reason } when the affordability check declined or referred it.
  const approveLoan = async (loanId, body = {}) => {
    try {
      const token = localStorage.getItem('token');
  await axios.put(apiUrl(`/api/loans/${loanId}/approve`), body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActionMessage('Loan approved');
      setActionType('success');
  // Refresh only the Manage Loans table
  refreshLoansOnly();
    } catch (error) {
  const msg = error?.response?.data?.error || error?.response?.data?.message || 'Error approving loan';
  setActionMessage(msg);
      setActionType('error');
      // LoanDetails shows the API's reason and stays open.
      throw error;
    }
  };

  const rejectLoan = async (loanId, rejectionReason = '') => {
    try {
      const token = localStorage.getItem('token');
  await axios.put(apiUrl(`/api/loans/${loanId}/reject`), {
        rejection_reason: rejectionReason
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActionMessage('Loan rejected');
      setActionType('error');
      refreshData();
    } catch (error) {
      setActionMessage(error?.response?.data?.error || error?.response?.data?.message || 'Error rejecting loan');
      setActionType('error');
    }
  };

  const markLoanBadDebt = async (loanId, note = '') => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(apiUrl(`/api/loans/${loanId}/mark-bad-debt`), { note }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActionMessage('Loan marked as Bad Debt');
      setActionType('success');
      refreshLoansOnly();
    } catch (error) {
      const msg = error?.response?.data?.error || error?.response?.data?.message || 'Failed to mark loan as Bad Debt';
      setActionMessage(msg);
      setActionType('error');
      throw new Error(msg);
    }
  };

  const writeOffLoan = async (loanId, note = '') => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(apiUrl(`/api/loans/${loanId}/write-off`), { note }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActionMessage('Loan written off');
      setActionType('success');
      refreshLoansOnly();
    } catch (error) {
      const msg = error?.response?.data?.error || error?.response?.data?.message || 'Failed to write off loan';
      setActionMessage(msg);
      setActionType('error');
      throw new Error(msg);
    }
  };

  const undoWriteOffLoan = async (loanId, note = '') => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(apiUrl(`/api/loans/${loanId}/undo-write-off`), { note }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActionMessage('Loan write-off undone');
      setActionType('success');
      refreshLoansOnly();
    } catch (error) {
      const msg = error?.response?.data?.error || error?.response?.data?.message || 'Failed to undo write-off';
      setActionMessage(msg);
      setActionType('error');
      throw new Error(msg);
    }
  };

  const rescheduleLoan = async (loanId, note = '', newDueDate = '') => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(apiUrl(`/api/loans/${loanId}/reschedule`), { note, newDueDate }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActionMessage('Loan rescheduled');
      setActionType('success');
      refreshLoansOnly();
    } catch (error) {
      const msg = error?.response?.data?.error || error?.response?.data?.message || 'Failed to reschedule loan';
      setActionMessage(msg);
      setActionType('error');
      throw new Error(msg);
    }
  };

  const openStatusConfirm = (kind, loanId, note = '') => {
    setStatusConfirmCtx({ kind, loanId, note });
  };
  const [writeOffRefresh, setWriteOffRefresh] = useState(0);

  // Add new function to revert loan to pending
  const revertLoanToPending = async (loanId) => {
    try {
      const token = localStorage.getItem('token');
  await axios.put(apiUrl(`/api/loans/${loanId}/revert-to-pending`), {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActionMessage('Loan status changed to pending');
      setActionType('success');
      refreshData();
    } catch (error) {
      const msg = error?.response?.data?.error || error?.response?.data?.message || 'Error changing loan status';
      setActionMessage(msg);
      setActionType('error');
    }
  };

  // Embedded in the Clients workspace the list covers a single client; standalone
  // it covers every loan. Filtering only narrows what is displayed.
  const scopedLoans = clientId == null
    ? loans
    : loans.filter(l => String(l.client_id ?? l.clientId) === String(clientId));

  const loansWithClient = scopedLoans.map(loan => ({
    ...loan,
    // server already populates clientName via JOIN; keep a sane fallback
    clientName: (typeof loan.clientName === 'string' && loan.clientName.trim())
      ? loan.clientName
      : ((loan.first_name || loan.last_name) ? `${loan.first_name || ''} ${loan.last_name || ''}`.trim() : 'Unknown'),
    repayments: loan.repayments || [],
    clientId: loan.client_id,
  }));

  // A signed contract exists once the loan is past the application/approval phase
  const hasViewableContract = (status) =>
    !['Pending', 'Awaiting Client', 'Awaiting Staff', 'Awaiting Approval', 'Rejected', 'Cancelled'].includes(status);

  const openClientContracts = (loan) => {
    const clientId = loan.client_id ?? loan.clientId;
    const clientLoans = loansWithClient.filter(l => (l.client_id ?? l.clientId) === clientId);
    setContractsModal({ clientId, clientName: loan.clientName || 'Unknown', loans: clientLoans });
    setContractsModalPage(1);
  };

  const viewClientContract = async (loanId) => {
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(apiUrl(`/api/contracts/${loanId}/view`), { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) { alert('Failed to open contract'); return; }
      const blob = await resp.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch {
      alert('Failed to open contract');
    }
  };

  // Table search: client name, loan code, client code or status. Scoped to one
  // client the name alone would be useless, so the loan's own fields count too.
  const filteredBySearch = loansWithClient.filter(loan => {
    if (!loan.clientName || typeof loan.clientName !== 'string') return false;
    if (!searchTerm) return true;
    const haystack = [
      loan.clientName,
      loan.loan_code || loan.loanCode || '',
      loan.client_code || loan.clientCode || '',
      loan.status || '',
    ].join(' ').toLowerCase();
    return haystack.includes(searchTerm.toLowerCase());
  });

  // Status filter via chips
  const filteredLoans = filteredBySearch.filter(l => {
    if (filterStatus === 'All') return true;
    if (filterStatus === 'Active') return Boolean(l.state?.is_active);
    if (filterStatus === 'Overdue') return Boolean(l.state?.is_overdue);
    // Approved: every loan that was approved, also once paid out or repaid.
    if (filterStatus === 'Approved') return Boolean(l.state?.approved);
    // Due in the next 7 days, nothing late yet (the API's state.due_soon).
    if (filterStatus === 'Due Soon') return Boolean(l.state?.due_soon);
    // Outstanding balance: the loans the Total Outstanding tile adds up.
    if (filterStatus === 'Owing') return owesBalance(l);
    // The affordability check's decision (affordability:Declined, ...).
    if (filterStatus.startsWith('affordability:')) return (l.affordability_decision || '') === filterStatus.slice('affordability:'.length);
    return (l.status || '') === filterStatus;
  });

  // Outstanding-first sorting:
  // The latest loans first: newest created on top (the loan id when two
  // were created at the same moment).
  const createdAt = (l) => {
    const t = new Date(l.created_at || 0).getTime();
    return Number.isFinite(t) ? t : 0;
  };
  const sortedLoans = [...filteredLoans].sort((a, b) =>
    (createdAt(b) - createdAt(a)) || ((Number(b.id) || 0) - (Number(a.id) || 0)));

  const pageSize = embedded ? EMBEDDED_PAGE_SIZE : PAGE_SIZE;
  const totalPages = Math.ceil(sortedLoans.length / pageSize) || 1;
  // Clamp, so filtering down to fewer loans cannot leave the list blank on a
  // page that no longer exists.
  const pageSafe = Math.min(currentPage, totalPages);
  // The table's rows for every match, so a click on a column sorts them all
  // before the page is taken (the newest first until a column is clicked).
  const loanRows = sortedLoans.map(loan => {
    // Outstanding: the API's total_due (penalties included), never redone here.
    const principalPlusInterest = Number(loan.principal_amount_plus_interest) || 0;
    const additionalExpensesTotal = Number(loan.additional_expenses_total) || 0;
    const grandTotalObligation = principalPlusInterest + additionalExpensesTotal;
    const outstanding = loan.total_due !== undefined && loan.total_due !== null
      ? toCurrency(loan.total_due)
      : '-';

    return {
      id: loan.id,
      loan_id: loan.id,
      client_id: loan.client_id ?? loan.clientId,
      loan_ref: loan.loan_code || loan.loanCode || '',
      client_ref: loan.client_code || loan.clientCode || '',
      clientName: loan.clientName || 'Unknown',
      principal_amount: (loan.principal_amount == null || loan.principal_amount === '-') ? '-' : toCurrency(loan.principal_amount),
      principal_amount_plus_interest: loan.principal_amount_plus_interest ? toCurrency(loan.principal_amount_plus_interest) : '-',
      grand_total_obligation: toCurrency(grandTotalObligation),
      interest_rate: loan.interest_rate ? `${loan.interest_rate}%` : '-',
      due_date: loan.due_date ? formatDateDMY(loan.due_date) : '-',
      outstanding,
      status: loan.status || '-',
      calculator: loan.calculator?.decision || null,
      loanData: loan
    };
  });
  const loanSort = useTableSort(loanRows);
  const paginatedLoans = loanSort.rows.slice((pageSafe - 1) * pageSize, pageSafe * pageSize);

  const handleShowLoanDetails = async (loan) => {
    // Only one in-page panel at a time
    setShowInvoiceModal(false);
    setInvoiceLoan(null);
    setSelectedLoan(loan);
    setShowLoanDetails(true);

    // Set documents to null to indicate loading
    setLoanDocuments(null);
    
  // Always fetch documents for the loan details modal so images show inline
  await fetchLoanDocuments(loan.id, loan);
  };

  const handleCloseLoanDetails = () => {
    setShowLoanDetails(false);
    setSelectedLoan(null);
  };

  // ?loan=<id> ("Open full page" from a loan quick look): open that loan's
  // details here once the list has loaded.
  const openedFromUrlRef = React.useRef(null);
  useEffect(() => {
    if (embedded || !loansLoaded) return;
    const id = new URLSearchParams(location.search).get('loan');
    if (!id || openedFromUrlRef.current === id) return;
    openedFromUrlRef.current = id;
    const found = (Array.isArray(loans) ? loans : []).find(l => String(l?.id) === String(id));
    if (found) {
      handleShowLoanDetails(found);
      return;
    }
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(apiUrl(`/api/loans/${id}`), { headers: { Authorization: `Bearer ${token}` } });
        if (res?.data?.id) handleShowLoanDetails(res.data);
      } catch {
        setError('That loan could not be opened.');
      }
    })();
  }, [location.search, loansLoaded, embedded]);

  const handleShowRepaymentForm = (loan, clientId) => {
    setRepaymentLoan(loan);
    setRepaymentClientId(clientId);
    setShowRepaymentForm(true);
  };

  const handleCloseRepaymentForm = () => {
    setShowRepaymentForm(false);
    setRepaymentLoan(null);
    setRepaymentClientId(null);
  };

  const handleShowEditForm = async (loan) => {
    setShowEditForm(true);
    setEditLoan(loan);
  };

  const handleCloseEditForm = () => {
    setShowEditForm(false);
    setEditLoan(null);
  };

  const handleShowRejectionForm = () => {};

  const handleShowCancelForm = (loan) => {
    setCancelLoanTarget(loan);
    setShowCancelForm(true);
    setShowLoanDetails(false);
  };

  const handleCloseCancelForm = () => {
    setShowCancelForm(false);
    setCancelLoanTarget(null);
  };

  const handleShowTerminateForm = (loan) => {
    setTerminateLoanTarget(loan);
    setShowTerminateForm(true);
    setShowLoanDetails(false);
  };

  const handleCloseTerminateForm = () => {
    setShowTerminateForm(false);
    setTerminateLoanTarget(null);
  };

  const cancelLoan = async (loanId, reason = '') => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(apiUrl(`/api/loans/${loanId}/cancel`), { cancel_reason: reason }, { headers: { Authorization: `Bearer ${token}` } });
      setActionMessage('Loan cancelled');
      setActionType('warning');
      refreshData();
    } catch (error) {
      const msg = error?.response?.data?.error || 'Error cancelling loan';
      setActionMessage(msg);
      setActionType('error');
    }
  };

  const terminateContract = async (loanId, note = '') => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(apiUrl(`/api/loans/${loanId}/terminate`), { termination_note: note }, { headers: { Authorization: `Bearer ${token}` } });
      setActionMessage('Contract terminated');
      setActionType('warning');
      refreshData();
    } catch (error) {
      const msg = error?.response?.data?.error || 'Error terminating contract';
      setActionMessage(msg);
      setActionType('error');
    }
  };

  // Opens the undo form on the loan's repayments; the API's suggestion
  // (state.staff_actions.undo_repayment) is chosen already, any can be picked.
  const openUndoRepaymentConfirm = (loan) => {
    const undo = loan.state?.staff_actions?.undo_repayment;
    if (!undo) {
      setActionMessage('No repayments to undo for this loan');
      setActionType('error');
      return;
    }
    setUndoCtx({ loanId: loan.id, loanCode: loan.loan_code, repaymentId: undo.id });
    setShowUndoConfirm(true);
  };

  const getMessageClass = () => {
    switch (actionType) {
      case 'success':
        return 'bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700/50';
      case 'error':
        return 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700/50';
      case 'warning':
        return 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700/50';
      default:
        return 'bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-700/50';
    }
  };

  // What staff may do with each loan comes from the API (loan.state.staff_actions:
  // status rules and this user's permissions applied). No state, no action.
  const staffCan = (loan, action) => Boolean(loan?.state?.staff_actions?.[action]);
  const canViewInvoice = (loan) => Boolean(loan?.state?.actions?.view_invoice);

  const openInvoice = async (loan) => {
    // Only one in-page panel at a time
    setShowLoanDetails(false);
    setSelectedLoan(null);
    try {
      const enriched = await enrichInvoiceLoan(loan);
      setInvoiceLoan(enriched);
      setShowInvoiceModal(true);
    } catch {
      setInvoiceLoan(loan);
      setShowInvoiceModal(true);
    }
  };


  // removed interest rates fetch (no longer needed)

  // Load notes from localStorage on mount
useEffect(() => {
  const storedNotes = localStorage.getItem('moreDetailsNotes');
  if (storedNotes) {
    try {
      setMoreDetailsNotes(JSON.parse(storedNotes));
    } catch {
      setMoreDetailsNotes({});
    }
  }
}, []);

// Save notes to localStorage whenever they change
useEffect(() => {
  localStorage.setItem('moreDetailsNotes', JSON.stringify(moreDetailsNotes));
}, [moreDetailsNotes]);

  // When loan status changes from pending, clear the note for that loan (in state and localStorage)
  useEffect(() => {
    setMoreDetailsNotes(prevNotes => {
      const updated = { ...prevNotes };
      let changed = false;
      loans.forEach(loan => {
        if (loan.status !== 'Pending' && updated[loan.id]) {
          delete updated[loan.id];
          changed = true;
        }
      });
      if (changed) {
        localStorage.setItem('moreDetailsNotes', JSON.stringify(updated));
      }
      return updated;
    });
  }, [loans]);

  // Remove note if loan details are updated (principal, interest, duration, deduction day)
  useEffect(() => {
    setMoreDetailsNotes(prevNotes => {
      // Find loans that have a note and have changed details
      const updated = { ...prevNotes };
      loans.forEach(loan => {
        if (!updated[loan.id]) return;
        // Find the original loan from the server (not the edited one)
        const origLoan = loans.find(l => l.id === loan.id);
        if (!origLoan) return;
        // If any of these fields changed, remove the note
        if (
          String(loan.principal_amount) !== String(origLoan.principal_amount) ||
          String(loan.interest_rate) !== String(origLoan.interest_rate) ||
          String(loan.duration_months) !== String(origLoan.duration_months) ||
          String(loan.deduction_day) !== String(origLoan.deduction_day)
        ) {
          delete updated[loan.id];
        }
      });
      localStorage.setItem('moreDetailsNotes', JSON.stringify(updated));
      return updated;
    });
  // Only run when loans change (after edit/save)
  }, [loans]);

  return (
    <div className={embedded ? 'max-w-full' : 'max-w-full min-h-screen'}>
      {/* Stat cards first, above the search and filters */}
      {/* All four on one row. Embedded, the panel is only half the content
          width, so the cards go compact rather than clipping a long amount.
          Two-up below sm, where four would be unreadable on a phone. */}
      <div className={`grid gap-3 mb-3 ${embedded ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'}`}>
        {(() => {
          const m = loansMetrics(scopedLoans);
          return (
            <>
              <KpiCard label="Total Outstanding" value={toCurrency(m.outstandingAmount)} color="blue" compact={embedded}
                subtitle={`${m.owingCount} loan${m.owingCount === 1 ? '' : 's'}`}
                onClick={() => { setFilterStatus('Owing'); setCurrentPage(1); }} title="Show the loans with a balance owing" />
              <KpiCard label="Overdue Loans" value={m.overdueCount} color="red" compact={embedded}
                onClick={() => { setFilterStatus('Overdue'); setCurrentPage(1); }} title="Show the overdue loans" />
              <KpiCard label="Approvals Pending" value={m.approvalsPending} color="amber" compact={embedded}
                onClick={() => { setFilterStatus('Awaiting Approval'); setCurrentPage(1); }} title="Show the loans awaiting approval" />
              {(() => {
                // The instalments due in the next 7 days: each loan's next
                // instalment as the API worked it out (state.due_soon).
                const dueSoon = scopedLoans.filter((l) => l.state?.due_soon);
                const amount = dueSoon.reduce((t, l) => t + (Number(l.state?.next_payment?.amount) || 0), 0);
                return (
                  <KpiCard label="Due in Next 7 Days" value={toCurrency(amount)} color="green" compact={embedded}
                    subtitle={`${dueSoon.length} loan${dueSoon.length === 1 ? '' : 's'}`}
                    onClick={() => { setFilterStatus('Due Soon'); setCurrentPage(1); }}
                    title="Show the loans with an instalment due in the next 7 days" />
                );
              })()}
            </>
          );
        })()}
      </div>

      {/* Toolbar — search then status on the left, export and paging hard right,
          all on one line. Search and status never split from each other: the
          search box shrinks instead, down to min-w-0. Only when the panel is
          too narrow for the actions as well do those drop to a second line. */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <input
            type="text"
            value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            placeholder={embedded ? 'Search loan code or status…' : 'Search client, loan code or status…'}
            aria-label="Search loans"
            className="toolbar-control flex-1 min-w-0"
          />
          <LoansStatusDropdown
            id={embedded ? 'loansStatusFilterEmbedded' : 'loansStatusFilter'}
            value={filterStatus}
            onChange={(v) => { setFilterStatus(v); setCurrentPage(1); }}
            className="flex-shrink-0"
            selectClassName={embedded ? 'min-w-[110px]' : 'min-w-[160px] sm:min-w-[200px]'}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2 ml-auto justify-end flex-shrink-0">
          {/* Removed "+ Add Loan" button per request */}
          {!embedded && (hasPerm('RunOverdueProcessor') || hasPerm('ManageLoans')) && (
            <Button
              onClick={async () => {
                try {
                  const token = localStorage.getItem('token');
                  if (!token) {
                    setActionMessage('(401) No token provided');
                    setActionType('error');
                    return;
                  }

                  await axios.post(
                    apiUrl('/api/test-overdue-loans'),
                    {},
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  setActionMessage('Overdue processor ran successfully');
                  setActionType('success');
                  refreshData();
                } catch (e) {
                  const status = e?.response?.status;
                  const serverError = e?.response?.data?.error || e?.response?.data?.message;
                  const details = e?.response?.data?.details;
                  const fallback = e?.message || 'Failed to run overdue processor';
                  const msgBase = serverError || fallback;
                  const statusPrefix = status ? `(${status}) ` : '';
                  const detailsSuffix = details ? `: ${details}` : '';
                  setActionMessage(`${statusPrefix}${msgBase}${detailsSuffix}`);
                  setActionType('error');
                }
              }}
              variant="secondary"
              size="md"
              className="toolbar-control"
              busyLabel="Running…"
            >
              Run Overdue Processor
            </Button>
          )}
          {(hasPerm('ExportLoans') || hasPerm('ManageLoans')) && (
            <Button
              onClick={() => {
                const exportRows = scopedLoans.map(l => ({
                  ...l,
                  loan_ref: l.loan_code || l.loanCode || '',
                  client_ref: l.client_code || l.clientCode || '',
                  due_date: l.due_date ? formatDateDMY(l.due_date) : '',
                }));
                const cols = [
                  { field: 'loan_ref', header: 'Loan Code' },
                  { field: 'client_ref', header: 'Client Code' },
                  { field: 'clientName', header: 'Client' },
                  { field: 'status', header: 'Status' },
                  { field: 'principal_amount', header: 'Principal' },
                  { field: 'principal_amount_plus_interest', header: 'Principal+Interest' },
                  { field: 'interest_rate', header: 'Rate' },
                  { field: 'due_date', header: 'Due Date' },
                  { field: 'total_due', header: 'Total Due' },
                ];
                exportToCsv('loans.csv', exportRows, cols);
              }}
              variant="secondary"
              size="md"
              className="toolbar-control"
            >
              Export CSV
            </Button>
          )}
          <Pager page={pageSafe} pages={totalPages} onPage={setCurrentPage} />
        </div>
      </div>

      {actionMessage && (
        <div className={`mb-2 p-3 rounded border flex items-center justify-between ${getMessageClass()}`}>
          <span>{actionMessage}</span>
          <button
            onClick={() => setActionMessage('')}
            className="ml-4 px-2 py-1 rounded-full text-lg font-bold hover:bg-gray-200 dark:hover:bg-slate-600 focus:outline-none"
            aria-label="Close"
            title="Close"
          >
            &times;
          </button>
        </div>
      )}

      {error && <div className="p-2 rounded-lg mb-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">{error}</div>}

      {!showLoanDetails && (
      <>
      {/* Write-off candidates (policy and prescription; never automatic) */}
      {!embedded && (hasPerm('*') || hasPerm('ViewLoans') || hasPerm('ManageLoans')) && (
        <WriteOffCandidatesPanel
          className="mb-3"
          refreshKey={writeOffRefresh}
          canWriteOff={hasPerm('*') || hasPerm('ManageLoans')}
          onWriteOff={(c, note) => openStatusConfirm('writeOff', c.loan_id, note)}
        />
      )}
      {/* Loans list - shared ResponsiveTable renders cards at all breakpoints */}
      <div className="bg-white dark:bg-slate-900">
        <ResponsiveTable
          data={paginatedLoans}
          sort={loanSort.sort}
          onSortChange={loanSort.onSortChange}
          columns={[
            { field: 'loan_ref', header: 'Loan Code', formatter: (value, row) => (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleShowLoanDetails(row.loanData); }}
                  className="text-left text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
                  title="Open loan details"
                >
                  {value || `Loan #${row.id}`}
                </button>
              ) },
            ...(embedded ? [] : [{ field: 'clientName', header: 'Client Name', formatter: (value, row) => (
                <ClientLink clientId={row.client_id} name={value} />
              ) }]),
            {
              field: 'calculator',
              header: 'Calculator',
              // The legal test's decision (Microlending Act / ML.S.4); a dash
              // where the loan was captured without a pre-qualification.
              sortValue: (row) => (CALCULATOR_BADGE[row.calculator]?.[0] || ''),
              formatter: (value) => {
                const badge = CALCULATOR_BADGE[value];
                if (!badge) return <span className="text-gray-400 dark:text-slate-500">—</span>;
                return (
                  <span className={`px-2 py-0.5 inline-flex items-center text-xs font-semibold border whitespace-nowrap ${badge[1]}`} title={value}>
                    {badge[0]}
                  </span>
                );
              },
            },
            { field: 'principal_amount', header: 'Principal Amount', sortValue: (row) => row.loanData.principal_amount },
            { field: 'due_date', header: 'Due Date', sortValue: (row) => row.loanData.due_date },
            { field: 'outstanding', header: 'Outstanding Amount', sortValue: (row) => row.loanData.total_due },
            { field: 'status', header: 'Loan Status', formatter: (value, row) => (
                <span className="inline-flex flex-wrap items-center gap-1">
                <StatusBadge status={value} />
                {/* The affordability check's decision; before it was kept, the calculator's. */}
                {row.loanData?.affordability_decision ? (
                  <AffordabilityBadge decision={row.loanData.affordability_decision} />
                ) : row.loanData?.calculator?.declined && (
                  <span
                    className="px-2 py-0.5 inline-flex text-[11px] font-semibold rounded-full border border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-700/60 dark:bg-rose-900/30 dark:text-rose-300"
                    title={row.loanData.calculator.warning || undefined}
                  >
                    Calculator declined
                  </span>
                )}
                </span>
              ) },
            { field: 'actions', header: 'Actions', formatter: (_, row) => (
                <div className="flex flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <ActionButton
                    onClick={(e) => {
                      e.stopPropagation();
                      return handleShowLoanDetails(row.loanData);
                    }}
                    className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300"
                  >
                    View Details
                  </ActionButton>

                  {canViewInvoice(row.loanData) && (
                    <ActionButton
                      onClick={(e) => {
                        e.stopPropagation();
                        return openInvoice(row.loanData);
                      }}
                      className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-amber-50 text-amber-600 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300"
                    >
                      View Invoice
                    </ActionButton>
                  )}

                  {!IN_PROCESS_STATUSES.includes(row.loanData.status) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openClientContracts(row.loanData);
                    }}
                    className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-indigo-50 text-indigo-600 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-300"
                  >
                    View Contracts
                  </button>
                  )}

                  {/* Repayment option for admin, manager, or staff */}
                  {staffCan(row.loanData, 'enter_repayment') && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleShowRepaymentForm(row.loanData, row.loanData.clientId);
                      }}
                      className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-purple-50 text-purple-600 hover:bg-purple-100 dark:bg-purple-900/30 dark:text-purple-300"
                    >
                      Enter Repayment
                    </button>
                  )}

                  {/* Edit option for admin, manager, or staff */}
                  {staffCan(row.loanData, 'edit') && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleShowEditForm(row.loanData);
                      }}
                      className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300"
                    >
                      Edit Loan
                    </button>
                  )}

                  {/* Cancel option (pre-approval loans) */}
                  {staffCan(row.loanData, 'cancel') && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleShowCancelForm(row.loanData);
                      }}
                      className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-rose-50 text-rose-600 hover:bg-rose-100 dark:bg-rose-900/30 dark:text-rose-300"
                    >
                      Cancel Loan
                    </button>
                  )}

                  {/* Terminate Contract option (permission gated) */}
                  {staffCan(row.loanData, 'terminate') && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleShowTerminateForm(row.loanData);
                      }}
                      className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-rose-50 text-rose-600 hover:bg-rose-100 dark:bg-rose-900/30 dark:text-rose-300"
                    >
                      Terminate Contract
                    </button>
                  )}

                  {/* Written off */}
                  {staffCan(row.loanData, 'write_off') && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openStatusConfirm('writeOff', row.loanData.id);
                      }}
                      className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-rose-50 text-rose-600 hover:bg-rose-100 dark:bg-rose-900/30 dark:text-rose-300"
                    >
                      Write Off
                    </button>
                  )}

                  {/* Undo write-off */}
                  {staffCan(row.loanData, 'undo_write_off') && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openStatusConfirm('undoWriteOff', row.loanData.id);
                      }}
                      className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-emerald-50 text-emerald-600 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300"
                    >
                      Undo Write-Off
                    </button>
                  )}

                  {/* Reschedule */}
                  {staffCan(row.loanData, 'reschedule') && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openStatusConfirm('reschedule', row.loanData.id);
                      }}
                      className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-amber-50 text-amber-600 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300"
                    >
                      Reschedule
                    </button>
                  )}

                  {/* Revert to Pending option for admin or manager */}
                  {staffCan(row.loanData, 'revert_to_pending') && (
                    <ActionButton
                      onClick={(e) => {
                        e.stopPropagation();
                        return revertLoanToPending(row.loanData.id);
                      }}
                      className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-purple-50 text-purple-600 hover:bg-purple-100 dark:bg-purple-900/30 dark:text-purple-300"
                    >
                      Revert to Pending
                    </ActionButton>
                  )}

                  {/* Undo last repayment (only if there is a repayment) */}
                  {staffCan(row.loanData, 'undo_repayment') && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openUndoRepaymentConfirm(row.loanData);
                      }}
                      className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300"
                    >
                      Undo Repayment
                    </button>
                  )}
                  {/* Put back an undone repayment (only when the loan has one). */}
                  {Number(row.loanData?.undone_repayments) > 0 && staffCan(row.loanData, 'enter_repayment') && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setUndoCtx({ loanId: row.loanData.id, loanCode: row.loanData.loan_code, mode: 'redo' });
                        setShowUndoConfirm(true);
                      }}
                      className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300"
                    >
                      Redo Repayment
                    </button>
                  )}
                </div>
             ) }
          ]}
          keyField="id"
          isLoading={false}
          emptyMessage="No results found."
          onRowClick={(row) => handleShowLoanDetails(row.loanData)}
          headerClassName="bg-gray-50 dark:bg-slate-800"
        />
      </div>
      
      </>
      )}

  {/* Loan Details Popout - moved to LoanDetails.jsx */}
      {showLoanDetails && selectedLoan && (
        <LoanDetails
          loan={selectedLoan}
          onClose={handleCloseLoanDetails}
          canApprove={staffCan(selectedLoan, 'approve')}
          canReject={staffCan(selectedLoan, 'reject')}
          canRevertToPending={staffCan(selectedLoan, 'revert_to_pending')}
          approveLoan={approveLoan}
          handleShowRejectionForm={handleShowRejectionForm}
          moreDetailsNotes={moreDetailsNotes}
          setMoreDetailsNotes={setMoreDetailsNotes}
          showNoteInput={showNoteInput}
          setShowNoteInput={setShowNoteInput}
          userRole={''}
          onSavedNote={() => {
            setActionMessage('Note saved and sent to client');
            setActionType('success');
            refreshData();
          }}
          onEdited={() => { setShowLoanDetails(false); setSelectedLoan(null); refreshData(); }}
          onApproved={() => {
            setShowLoanDetails(false);
            setSelectedLoan(null);
            if (embedded) {
              setActionMessage('Loan approved');
              setActionType('success');
              refreshLoansOnly();
            } else {
              navigate('/admin');
            }
          }}
        />
      )}

      {/* Invoice Modal (shared with client-side) */}
      {showInvoiceModal && invoiceLoan && (
        <InvoiceModal
          visible={showInvoiceModal}
          loan={invoiceLoan}
          onClose={() => { setShowInvoiceModal(false); setInvoiceLoan(null); }}
        />
      )}

      {/* Client Contracts Modal */}
      {contractsModal && (() => {
        const PAGE = 5;
        const totalPages = Math.max(1, Math.ceil(contractsModal.loans.length / PAGE));
        const page = Math.min(contractsModalPage, totalPages);
        const pageLoans = sortRows(contractsModal.loans, contractsSort).slice((page - 1) * PAGE, page * PAGE);
        return (
          <FullScreenPanel
            isOpen
            onClose={() => setContractsModal(null)}
            title={`Contracts — ${contractsModal.clientName}`}
            subtitle={`${contractsModal.loans.length} loan${contractsModal.loans.length === 1 ? '' : 's'} on record`}
          >
            {contractsModal.loans.length === 0 ? (
              <div className="text-center py-8 text-sm text-gray-400 dark:text-slate-500">No contracts found for this client.</div>
            ) : (
              <div className="space-y-3">
                <ResponsiveTable
                  data={pageLoans}
                  sort={contractsSort}
                  onSortChange={setContractsSort}
                  columns={[
                    { field: 'loan_code', header: 'Loan Code', formatter: (v, loan) => v || loan.loanCode || '-' },
                    { field: 'principal_amount', header: 'Principal', formatter: (v) => (v ? toCurrency(v) : '-') },
                    { field: 'due_date', header: 'Due Date', formatter: (v) => (v ? formatDateDMY(v) : '-') },
                    {
                      field: 'status',
                      header: 'Status',
                      formatter: (status) => (
                        <StatusBadge status={status} label={status || '-'} />
                      ),
                    },
                    {
                      field: 'id',
                      header: 'Actions',
                      formatter: (id, loan) => (hasViewableContract(loan.status) ? (
                        <ActionButton
                          type="button"
                          onClick={() => viewClientContract(id)}
                          className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300"
                          busyLabel="Opening…"
                        >
                          View Contract
                        </ActionButton>
                      ) : (
                        <span className="text-xs text-gray-400 dark:text-slate-500">No signed contract yet ({loan.status || 'Unknown'})</span>
                      )),
                    },
                  ]}
                />

                {totalPages > 1 && (
                  <Pager page={page} pages={totalPages} onPage={setContractsModalPage} className="justify-center pt-2" />
                )}
              </div>
            )}
          </FullScreenPanel>
        );
      })()}

      {/* Loan Edit Form Popout */}
      {showEditForm && editLoan && (
        <LoanEditForm
          loan={editLoan}
          onClose={() => setShowEditForm(false)}
          onSaved={() => {
            setShowEditForm(false);
            setEditLoan(null);
            refreshData();
          }}
        />
      )}

      {/* Repayment Form Modal */}
      {showRepaymentForm && repaymentLoan && (
        <RepaymentForm
          loan={repaymentLoan}
          clientId={repaymentClientId}
          onClose={handleCloseRepaymentForm}
          onSuccess={() => {
            handleCloseRepaymentForm();
            setActionMessage('Repayment entered successfully');
            setActionType('success');
            refreshData();
          }}
        />
      )}

      {/* Cancel Form Modal */}
      {showCancelForm && cancelLoanTarget && (
        <CancelForm
          loan={cancelLoanTarget}
          onClose={handleCloseCancelForm}
          onSuccess={() => {
            handleCloseCancelForm();
            setActionMessage('Loan cancelled successfully');
            setActionType('warning');
            refreshData();
          }}
        />
      )}

      {/* Terminate Contract Form Modal */}
      {showTerminateForm && terminateLoanTarget && (
        <TerminateContractForm
          loan={terminateLoanTarget}
          onClose={handleCloseTerminateForm}
          onSuccess={() => {
            handleCloseTerminateForm();
            setActionMessage('Contract terminated successfully');
            setActionType('warning');
            refreshData();
          }}
        />
      )}

      {/* Undo / Redo Repayment form */}
      {showUndoConfirm && undoCtx && (
        <UndoRepaymentConfirm
          ctx={undoCtx}
          mode={undoCtx.mode || 'undo'}
          onClose={() => { setShowUndoConfirm(false); setUndoCtx(null); }}
          onSuccess={async () => {
            const redone = undoCtx.mode === 'redo';
            setShowUndoConfirm(false);
            setUndoCtx(null);
            setActionMessage(redone ? 'Repayment put back' : 'Repayment undone');
            setActionType('warning');
            await refreshLoansOnly();
          }}
        />
      )}

      {/* Bad Debt / Write Off Confirmation Modal */}
      {statusConfirmCtx && (
        <LoanStatusConfirmModal
          kind={statusConfirmCtx.kind}
          initialNote={statusConfirmCtx.note || ''}
          onClose={() => setStatusConfirmCtx(null)}
          onConfirm={async (note, extra = {}) => {
            if (statusConfirmCtx.kind === 'writeOff') {
              await writeOffLoan(statusConfirmCtx.loanId, note);
              setWriteOffRefresh((k) => k + 1);
            } else if (statusConfirmCtx.kind === 'undoWriteOff') {
              await undoWriteOffLoan(statusConfirmCtx.loanId, note);
            } else if (statusConfirmCtx.kind === 'reschedule') {
              await rescheduleLoan(statusConfirmCtx.loanId, note, extra.newDueDate);
            } else {
              await markLoanBadDebt(statusConfirmCtx.loanId, note);
            }
            setStatusConfirmCtx(null);
          }}
        />
      )}

      {/* Removed Add Loan Modal per request */}
    </div>
  );
};

export default ManageLoans;